import React, { useEffect, useState } from 'react';
import { LoaderCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchConversationsApi, updateConversationApi } from '../../api/conversations.api';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useI18n } from '../../hooks/useI18n';
import { useSettingsModal } from './SettingsModalContext';

export const ArchivedChatsDialog = ({ open, onClose }) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { closeSettings } = useSettingsModal();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const conversations = await fetchConversationsApi({ archived: true });
        if (!cancelled) {
          setItems(conversations);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || t('archivedChatsLoadError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, t]);

  const handleOpen = async (conversation) => {
    setBusyId(conversation.id);
    try {
      await updateConversationApi(conversation.id, { isArchived: false });
      onClose();
      closeSettings();
      navigate(`/chat/${conversation.id}`);
    } catch (requestError) {
      setError(requestError.message || t('archivedChatsLoadError'));
    } finally {
      setBusyId('');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('archivedChatsTitle')}
      overlayClassName="z-[70]"
      panelClassName="max-w-lg rounded-2xl p-4"
      contentClassName="mb-3"
    >
      {loading ? (
        <div className="flex min-h-[140px] items-center justify-center text-neutral-400">
          <LoaderCircle size={22} className="animate-spin" aria-hidden />
        </div>
      ) : error ? (
        <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-200">
          {error}
        </p>
      ) : items.length === 0 ? (
        <p className="py-10 text-center text-sm text-neutral-400">{t('archivedChatsEmpty')}</p>
      ) : (
        <ul className="max-h-[min(24rem,50vh)] space-y-1 overflow-y-auto">
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                disabled={Boolean(busyId)}
                onClick={() => handleOpen(item)}
                className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-start text-sm hover:bg-neutral-50 dark:hover:bg-neutral-900"
              >
                <span className="truncate font-medium">{item.title || t('newConversation')}</span>
                <span className="shrink-0 text-xs text-neutral-400">
                  {busyId === item.id ? <LoaderCircle size={14} className="animate-spin" /> : t('archivedChatOpen')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 flex justify-end">
        <Button type="button" variant="secondary" onClick={onClose}>
          {t('close')}
        </Button>
      </div>
    </Modal>
  );
};
